/**
 * @file Read-only category-safe detail drawer for a single HI alert or review.
 * @module pages/admin/hi-alerts/HiAlertDetailDrawer
 *
 * B1.3H-3R-3 — fetches a single review via GET /admin/hi-alerts/{type}/{id} and
 * renders only the category-only projection fields, lifecycle timestamps and the
 * internal patient id. It never displays a raw IHI, DVA file number, Medicare
 * number, IRN, demographics or any match count. For an open amendable no-match
 * review (UC.015 `no_match`, the three UC.010 registration no-match reasons since
 * CU-14ygtkj8z3m, and the UC.015 Medicare/address no-match reviews since CU-14ygtkj98pb)
 * it offers the amend/resubmit action; it performs no auto-resolution or supersession.
 *
 * B1.3H-3S-1 — adds the operator-confirmed resolve action for an open review. The
 * resolve control is shown only while the review is open (the drawer itself is only
 * reachable inside the already approved-user-gated HI Reviews route/sidebar, so the
 * approved-user gate is inherited, exactly as the amend control inherits it). The
 * action requires an explicit confirmation step, calls the existing HI alerts API
 * client's resolve endpoint with an empty body (no operator notes are captured, so
 * no free-text/identifier can ride along) and, on success, refreshes the list and
 * detail caches so both reflect the resolved state. The originating no-match review
 * is never auto-resolved by an amended search — only this explicit action resolves
 * it. Confirmation and feedback wording is category-safe and value-free.
 *
 * B1.3H-3W-5D — adds the backend-projected review severity to the existing
 * category-safe description list, through the same shared display contract the list
 * column uses. The backend derives severity from the persisted bounded review reason
 * (`authentication#128`); this drawer only presents the category it receives.
 *
 * B3.3 (CU-14ygtkj8z2x) — type-aware: keyed by type + id, renders only the fields
 * projected for that type, and offers amend only where `canAmendReview()` narrows.
 */

import React, { useEffect, useState } from "react";
import { Drawer, Descriptions, Button, Spin, Empty, Alert, Modal } from "antd";
import { SearchOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { getHiAlert, resolveHiAlert } from "../../../api/admin/hiAlertsApi";
import {
	HiAlertReview,
	HiAlertReviewRef,
	HiAlertReviewType,
	HiAlertStatus,
	IHI_CONFLICT_TYPE,
	IHI_DEMOGRAPHIC_CONFLICT_TYPE,
	IHI_REVALIDATION_REVIEW_TYPE,
	IhiRevalidationReview,
	canAmendReview,
	canResolveReview,
} from "../../../types/admin/hiAlerts";
import {
	reviewReasonLabel,
	formatDateTime,
	patientLabel,
	reviewSeverityPresentation,
	hiAlertTypeLabel,
} from "./hiAlertLabels";
import StatusTag from "../../../components/shared/StatusTag";

interface HiAlertDetailDrawerProps {
	open: boolean;
	reviewRef: HiAlertReviewRef | null;
	onClose: () => void;
	onAmend: (review: IhiRevalidationReview) => void;
}

const DRAWER_TITLES: Record<HiAlertReviewType, string> = {
	[IHI_CONFLICT_TYPE]: "IHI conflict alert",
	[IHI_DEMOGRAPHIC_CONFLICT_TYPE]: "Demographic conflict alert",
	[IHI_REVALIDATION_REVIEW_TYPE]: "IHI revalidation review",
};

const drawerTitle = (type: string | null): string =>
	type !== null && Object.prototype.hasOwnProperty.call(DRAWER_TITLES, type)
		? DRAWER_TITLES[type as HiAlertReviewType]
		: "HI review";

const statusLabel = (status: HiAlertStatus): string => (status === "open" ? "Open" : "Resolved");

/**
 * Map a resolve failure to a safe, neutral operator message keyed on the backend's
 * authoritative status code. No backend body, identifier or demographic is echoed,
 * so the message stays category-only.
 */
const resolveErrorMessage = (error: unknown): string => {
	if (axios.isAxiosError(error) && error.response) {
		switch (error.response.status) {
			case 404:
				return "This review could not be found. It may already be resolved or no longer available.";
			case 409:
				return "This review is no longer open and cannot be resolved.";
			case 422:
				return "This review could not be resolved. Please try again.";
			default:
				return "Could not resolve the review. Please try again.";
		}
	}
	return "Could not resolve the review. Please try again.";
};

// Each branch names its fields explicitly so an unprojected or injected property is never rendered (CU-14ygtkj8z2x).
const DetailRows: React.FC<{ review: HiAlertReview }> = ({ review }) => {
	if (review.type === IHI_CONFLICT_TYPE) {
		return (
			<Descriptions column={1} size="small" bordered>
				<Descriptions.Item label="Alert ID">{review.id}</Descriptions.Item>
				<Descriptions.Item label="Type">{hiAlertTypeLabel(review.type)}</Descriptions.Item>
				<Descriptions.Item label="Requirement">{review.requirement_id}</Descriptions.Item>
				<Descriptions.Item label="Alert type">{reviewReasonLabel(review.alert_type)}</Descriptions.Item>
				<Descriptions.Item label="Status">{statusLabel(review.status)}</Descriptions.Item>
				<Descriptions.Item label="Assigned patient">{patientLabel(review.assigned_user_id)}</Descriptions.Item>
				<Descriptions.Item label="Conflicting patient">{patientLabel(review.conflicting_user_id)}</Descriptions.Item>
				<Descriptions.Item label="Detected">{formatDateTime(review.detected_at)}</Descriptions.Item>
				<Descriptions.Item label="Resolved">{formatDateTime(review.resolved_at)}</Descriptions.Item>
			</Descriptions>
		);
	}

	if (review.type === IHI_DEMOGRAPHIC_CONFLICT_TYPE) {
		return (
			<Descriptions column={1} size="small" bordered>
				<Descriptions.Item label="Alert ID">{review.id}</Descriptions.Item>
				<Descriptions.Item label="Type">{hiAlertTypeLabel(review.type)}</Descriptions.Item>
				<Descriptions.Item label="Requirement">{review.requirement_id}</Descriptions.Item>
				<Descriptions.Item label="Decision">{reviewReasonLabel(review.decision)}</Descriptions.Item>
				<Descriptions.Item label="Registration source">{review.registration_source ?? "—"}</Descriptions.Item>
				<Descriptions.Item label="Search type">{review.search_type ?? "—"}</Descriptions.Item>
				<Descriptions.Item label="Number status">{review.number_status ?? "—"}</Descriptions.Item>
				<Descriptions.Item label="Record status">{review.record_status ?? "—"}</Descriptions.Item>
				<Descriptions.Item label="Status">{statusLabel(review.status)}</Descriptions.Item>
				<Descriptions.Item label="Assigned patient">{patientLabel(review.assigned_user_id)}</Descriptions.Item>
				<Descriptions.Item label="Matching patient">{patientLabel(review.matching_user_id)}</Descriptions.Item>
				<Descriptions.Item label="Detected">{formatDateTime(review.detected_at)}</Descriptions.Item>
				<Descriptions.Item label="Resolved">{formatDateTime(review.resolved_at)}</Descriptions.Item>
			</Descriptions>
		);
	}

	if (review.type === IHI_REVALIDATION_REVIEW_TYPE) {
		// B1.3H-3W-5D — the same shared display contract the list column uses, so the
		// drawer and the list can never present a review's severity differently.
		const severity = reviewSeverityPresentation(review.severity);
		return (
			<Descriptions column={1} size="small" bordered>
				<Descriptions.Item label="Review ID">{review.id}</Descriptions.Item>
				<Descriptions.Item label="Type">{hiAlertTypeLabel(review.type)}</Descriptions.Item>
				<Descriptions.Item label="Requirement">{review.requirement_id}</Descriptions.Item>
				<Descriptions.Item label="Reason">{reviewReasonLabel(review.review_reason)}</Descriptions.Item>
				<Descriptions.Item label="Severity">
					<StatusTag status={severity.severity} label={severity.label} color={severity.color} />
				</Descriptions.Item>
				<Descriptions.Item label="Status">{statusLabel(review.status)}</Descriptions.Item>
				<Descriptions.Item label="Number status">{review.number_status ?? "—"}</Descriptions.Item>
				<Descriptions.Item label="Record status">{review.record_status ?? "—"}</Descriptions.Item>
				<Descriptions.Item label="IHI type">{review.ihi_type ?? "—"}</Descriptions.Item>
				<Descriptions.Item label="Patient">{patientLabel(review.patient_user_id)}</Descriptions.Item>
				<Descriptions.Item label="Detected">{formatDateTime(review.detected_at)}</Descriptions.Item>
				<Descriptions.Item label="Resolved">{formatDateTime(review.resolved_at)}</Descriptions.Item>
			</Descriptions>
		);
	}

	return <Empty description="Unsupported review type" />;
};

const HiAlertDetailDrawer: React.FC<HiAlertDetailDrawerProps> = ({ open, reviewRef, onClose, onAmend }) => {
	const queryClient = useQueryClient();

	const type = reviewRef?.type ?? null;
	const id = reviewRef?.id ?? null;

	// Transient resolve UI state. This component stays mounted under
	// HiAlertReviewPage between opens (the Drawer's destroyOnClose only clears the
	// rendered content, not the component), so it is reset below whenever the drawer
	// opens/closes or the target review changes — otherwise a prior review's
	// confirmation, success or error could bleed into the next one.
	const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
	const [resolved, setResolved] = useState<boolean>(false);
	const [error, setError] = useState<string>("");

	const { data: review, isLoading } = useQuery({
		queryKey: ["hi-alert", type, id],
		queryFn: () => getHiAlert(type as HiAlertReviewType, id as number),
		enabled: open && type !== null && id !== null,
	});

	// The resolve mutation is stamped with the type + id it was started for, so a
	// completion that lands after the drawer has moved on to another review (the
	// component stays mounted and only reviewRef changes) can be recognised as stale
	// and prevented from mutating the currently rendered review's UI.
	const resolveMutation = useMutation<HiAlertReview, unknown, HiAlertReviewRef>({
		mutationFn: (variables) => resolveHiAlert(variables.type, variables.id),
		onSuccess: (_data, variables) => {
			// The backend lifecycle change is authoritative regardless of what the UI
			// shows next: refresh both the list feed and this detail so the resolved
			// state is reflected in each. The detail refetch flips Status to Resolved
			// and drops both the resolve and amend controls.
			queryClient.invalidateQueries({ queryKey: ["hi-alerts"] });
			queryClient.invalidateQueries({ queryKey: ["hi-alert"] });
			// Stale completion: the resolved review is no longer the one on screen, so
			// keep the cache refresh above but do not touch this review's local UI state.
			if (variables.type !== type || variables.id !== id) {
				return;
			}
			setConfirmOpen(false);
			setResolved(true);
		},
		onError: (err, variables) => {
			// Same stale-completion guard: a failure for a review no longer shown must
			// not surface an error against the currently rendered review.
			if (variables.type !== type || variables.id !== id) {
				return;
			}
			setConfirmOpen(false);
			setError(resolveErrorMessage(err));
		},
	});

	useEffect(() => {
		setConfirmOpen(false);
		setResolved(false);
		setError("");
		resolveMutation.reset();
		// resolveMutation identity is stable enough for this reset-on-open/change guard.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, type, id]);

	// The amend control (open amendable no-match revalidation review only — the predicate
	// narrows the type) and the resolve control (any open review) disappear once resolved here.
	const amendable = review && !resolved && canAmendReview(review) ? review : null;
	const showResolve = !!review && !resolved && canResolveReview(review);

	return (
		<Drawer title={drawerTitle(type)} open={open} onClose={onClose} width={460} destroyOnClose>
			{isLoading && <Spin />}
			{!isLoading && !review && <Empty description="Review not found" />}
			{!isLoading && review && (
				<>
					<DetailRows review={review} />

					{error && (
						<Alert
							type="error"
							closable
							onClose={() => setError("")}
							style={{ marginTop: 16 }}
							message={error}
						/>
					)}

					{resolved && (
						<Alert
							type="success"
							showIcon
							style={{ marginTop: 16 }}
							message="This review has been resolved."
						/>
					)}

					{(amendable || showResolve) && (
						<div style={{ marginTop: 16, display: "flex", gap: 8 }}>
							{amendable && (
								<Button type="primary" icon={<SearchOutlined />} onClick={() => onAmend(amendable)}>
									Amend search
								</Button>
							)}
							{showResolve && (
								<Button icon={<CheckCircleOutlined />} onClick={() => setConfirmOpen(true)}>
									Resolve review
								</Button>
							)}
						</div>
					)}

					<Modal
						title="Resolve this review?"
						open={confirmOpen}
						onOk={() => resolveMutation.mutate({ type: type as HiAlertReviewType, id: id as number })}
						onCancel={() => {
							// Guard the Cancel path while the resolve is in flight so it cannot
							// close the confirmation and leave a pending mutation to complete
							// against a different review.
							if (resolveMutation.isPending) {
								return;
							}
							setConfirmOpen(false);
						}}
						okText="Yes, resolve review"
						cancelText="Cancel"
						confirmLoading={resolveMutation.isPending}
						cancelButtonProps={{ disabled: resolveMutation.isPending }}
						maskClosable={!resolveMutation.isPending}
						closable={!resolveMutation.isPending}
						keyboard={!resolveMutation.isPending}
					>
						<p style={{ margin: 0 }}>
							This marks the review as resolved and removes it from the open-review list. It
							cannot be reopened from here.
						</p>
					</Modal>
				</>
			)}
		</Drawer>
	);
};

export default HiAlertDetailDrawer;
